/**
 * Channel licences, as both portals show them.
 *
 * The server decides everything here -- which calls are allowed, whether a
 * client sees usage charges -- and the screens only report it. In particular
 * `shows_usage_costs` is a courtesy for the layout: the API already strips
 * charges from every response for a client on a flat monthly licence.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export type LicenseStatus =
  | 'not_enforced'
  | 'no_expiry'
  | 'active'
  | 'expiring'
  | 'grace'
  | 'expired';

export type BillingMode = 'MONTHLY' | 'PER_MINUTE' | 'MONTHLY_PLUS_USAGE';

export interface EngineLicense {
  kind: 'AI' | 'SOUNDBOARD';
  label: string;
  channels: number;
  in_use: number;
  monthly_price: number;
}

export interface LicenseInvoice {
  id: string;
  number: string;
  status: string;
  kind: 'LICENSE' | 'USAGE';
  period_start: string;
  period_end: string;
  total: number;
  amount_paid: number;
  balance: number;
  currency: string;
  due_at: string | null;
  paid_at: string | null;
  client_name?: string;
  tenant_id?: string;
}

export interface LicenseView {
  enforced: boolean;
  status: LicenseStatus;
  expires_at: string | null;
  grace_days: number;
  grace_ends_at: string | null;
  days_left: number | null;
  engines: EngineLicense[];
  monthly_total: number;
  currency: string;
  billing_mode: BillingMode;
  shows_usage_costs: boolean;
  client?: { id: string; name: string; status: string };
  open_invoices?: LicenseInvoice[];
}

export const STATUS_META: Record<
  LicenseStatus,
  { label: string; tone: 'gray' | 'green' | 'amber' | 'red' | 'blue' }
> = {
  not_enforced: { label: 'No licence', tone: 'gray' },
  no_expiry: { label: 'Active', tone: 'green' },
  active: { label: 'Active', tone: 'green' },
  expiring: { label: 'Expiring soon', tone: 'amber' },
  grace: { label: 'In grace period', tone: 'red' },
  expired: { label: 'Expired', tone: 'red' },
};

export const BILLING_MODE_LABELS: Record<BillingMode, string> = {
  MONTHLY: 'Monthly licence',
  PER_MINUTE: 'Per minute',
  MONTHLY_PLUS_USAGE: 'Monthly + usage',
};

export const BILLING_MODE_HINTS: Record<BillingMode, string> = {
  MONTHLY: 'Flat price per channel. Usage costs (STT, TTS, AI, telephony) are hidden from the client.',
  PER_MINUTE: 'Billed on usage. The client sees what each call cost.',
  MONTHLY_PLUS_USAGE: 'Channel licence plus usage. The client sees both.',
};

/**
 * The signed-in client's own licence. Disabled in the platform portal, where
 * there is no "own" licence to read.
 */
export function useMyLicense() {
  const { user } = useAuth();
  const isClient = !!user && user.actor !== 'platform';
  return useQuery({
    queryKey: ['my-license'],
    queryFn: () => api.get<LicenseView>('/account/license'),
    enabled: isClient,
    staleTime: 60_000,
  });
}

/**
 * Whether usage charges belong on this screen. True while loading and on
 * error, which is what every client saw before billing modes existed -- and
 * the API has already removed the numbers if the answer is no.
 */
export function useShowsUsageCosts(): boolean {
  const { data } = useMyLicense();
  return data?.shows_usage_costs ?? true;
}

export function daysLeftText(view: Pick<LicenseView, 'days_left' | 'status' | 'grace_ends_at'>): string {
  if (view.days_left === null) return 'No expiry';
  const days = Math.ceil(view.days_left);
  if (view.status === 'grace') return 'Past expiry — grace period';
  if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
  if (days === 0) return 'Ends today';
  return `${days} day${days === 1 ? '' : 's'} left`;
}
